import { useMemo } from 'react';

import { useCrossProductUrlWrapper } from '@atlassiansox/analytics-cross-product-interaction-client';

import { fg } from '@atlaskit/platform-feature-flags';

import { type PrimaryProductKey } from '../../constants/product-keys';
import { is1pProductKey, type ProductKeys1P } from '../../constants/product-keys';

/**
 * Bridge identifier for the cross-product analytics interaction client.
 *
 * - `quickFind` corresponds to the Quick Find search dialog experience.
 * - `searchPage` corresponds to the full Search Page experience.
 *
 * Single camelCase tokens are used here to mirror the convention set by
 * existing AFM bridges such as `appSwitcher`, `globalShortcuts`, and
 * `universalCreate`.
 */
export type CrossProductBridge = 'quickFind' | 'searchPage';

/**
 * Explicitly OPT IN a non-1P product to cross-product URL wrapping.
 *
 * Use sparingly — most products that should be wrapped are expressible via
 * the 1P default rule. Typed loosely as `Record<string, true>` because, by
 * definition, these keys are not in {@link ProductKeys1P}.
 */
const ADDITIONAL_WRAPPABLE_PRODUCTS: Readonly<
	Record<'goals' | 'projects' | 'loom' | 'teams', true>
> = {
	goals: true,
	projects: true,
	loom: true,
	teams: true,
};

/**
 * Explicitly OPT OUT a 1P product from cross-product URL wrapping.
 *
 * Typed against {@link ProductKeys1P} so a stale entry (e.g. after a 1P key
 * is removed from the master `ProductKeys1P` constant) fails the build.
 */
const NON_WRAPPABLE_1P: Readonly<Partial<Record<ProductKeys1P, true>>> = {
	// Add 1P products to opt out here, e.g. 'bitbucket': true
};

/**
 * Decide whether a result product should have its URL wrapped with
 * cross-product interaction tracking parameters.
 *
 * Decision priority (highest first):
 * 1. Explicit deny-list (`NON_WRAPPABLE_1P`) → never wrap.
 * 2. Explicit allow-list (`ADDITIONAL_WRAPPABLE_PRODUCTS`) → always wrap.
 * 3. Default rule → wrap iff the product is 1P.
 *
 * Exported for the guardrail unit test that asserts the lists don't
 * contradict the default rule.
 */
export const shouldWrapCrossProductUrl = (resultProduct: string): boolean => {
	if (resultProduct in NON_WRAPPABLE_1P) {
		return false;
	}
	if (resultProduct in ADDITIONAL_WRAPPABLE_PRODUCTS) {
		return true;
	}
	return is1pProductKey(resultProduct);
};

/** Re-exports for the guardrail test. Not part of the public API. */
export const __TEST_ONLY: {
	ADDITIONAL_WRAPPABLE_PRODUCTS: typeof ADDITIONAL_WRAPPABLE_PRODUCTS;
	NON_WRAPPABLE_1P: typeof NON_WRAPPABLE_1P;
} = {
	ADDITIONAL_WRAPPABLE_PRODUCTS,
	NON_WRAPPABLE_1P,
};

/**
 * Returns a URL wrapper that adds cross-product interaction tracking
 * parameters (`?xpis=...`) to result URLs that:
 *
 * - belong to a 1P product (per `ProductKeys1P`, with allow/deny overrides), AND
 * - point to a different product than the host (`primaryProduct`).
 *
 * For all other URLs (3P, same-product, missing product, missing
 * primaryProduct, feature flag off) it returns the URL unchanged.
 *
 * @example
 * const wrapUrl = useSearchCrossProductUrlWrapper({
 *   bridge: 'searchPage',
 *   primaryProduct, // e.g. 'jira'
 * });
 * const finalUrl = wrapUrl(rawUrl, ProductKeys.Confluence);
 */
export const useSearchCrossProductUrlWrapper = (args: {
	bridge: CrossProductBridge;
	primaryProduct: PrimaryProductKey | undefined;
}): ((url: string | undefined, resultProduct: string | undefined) => string | undefined) => {
	const { bridge, primaryProduct } = args;

	// `useCrossProductUrlWrapper` requires a non-empty `product` string. When
	// `primaryProduct` is undefined the outer guard below short-circuits and
	// the wrapper is never invoked, so this fallback is only there to keep
	// the hook call legal.
	const wrapUrl = useCrossProductUrlWrapper({
		bridge,
		product: primaryProduct ?? 'unknown',
	});

	const isEnabled = fg('rovo_search_cross_product_url_wrapping');

	return useMemo(
		() => (url, resultProduct) => {
			if (!isEnabled || !url || !primaryProduct || !resultProduct) {
				return url;
			}
			if (!shouldWrapCrossProductUrl(resultProduct)) {
				return url;
			}
			if (resultProduct === primaryProduct) {
				return url;
			}
			return wrapUrl(url);
		},
		[wrapUrl, primaryProduct, isEnabled],
	);
};
